import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from './ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Loader2,
  User,
  Mail,
  Phone,
  Trash2,
  Calendar,
  CheckCircle,
  XCircle,
  Send,
  Edit,
  Save
} from 'lucide-react';
import contactService from '../services/contactService';
import { useAuth } from '../contexts/AuthContext';

export default function StopContacts({ stopId, customerId, onUpdate }) {
  const { user } = useAuth();
  
  // Independent state - NO CACHING
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Debug logging
  console.log('🔍 StopContacts INDEPENDENT - rendered with:', {
    stopId,
    customerId,
    customerIdType: typeof customerId,
    customerIdValid: !!customerId,
    timestamp: Date.now()
  });
  
  // Direct fetch function - NO HOOKS, NO CACHING
  const fetchContacts = async () => {
    if (!customerId) {
      console.log('❌ StopContacts INDEPENDENT - No customer ID provided');
      setLoading(false);
      setError(new Error('No customer ID provided'));
      return;
    }

    console.log('🚀 StopContacts INDEPENDENT - Making direct API call for:', customerId);
    setLoading(true);
    setError(null);

    try {
      // Direct API call using contactService - bypasses all caching
      const result = await contactService.getCustomerContacts(customerId);
      console.log('✅ StopContacts INDEPENDENT - Received contacts:', result);
      
      setContacts(result || []);
      setLoading(false);
    } catch (err) {
      console.error('❌ StopContacts INDEPENDENT - Error fetching contacts:', err);
      setError(err);
      setLoading(false);
      setContacts([]);
    }
  };

  // Fetch on mount and when customerId changes
  useEffect(() => {
    console.log('🔄 StopContacts INDEPENDENT - Effect triggered, will fetch contacts');
    fetchContacts();
  }, [customerId]);
  
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [syncNotes, setSyncNotes] = useState('');
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');

  const isRSM = user?.role === 'RSM';
  const isBM = user?.role === 'BM';

  // Show error toast if there's an error loading contacts
  useEffect(() => {
    if (error) {
      console.error('Error fetching customer contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load contacts',
        variant: 'destructive'
      });
    }
  }, [error]);

  const removeContact = async (contactId, engagementType) => {
    try {
      await contactService.removeContactFromStop(stopId, contactId, engagementType);
      toast({
        title: 'Success',
        description: 'Contact removed from stop'
      });
      // Refetch contacts directly
      fetchContacts();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error removing contact:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove contact',
        variant: 'destructive'
      });
    }
  };

  const toggleContactSelection = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const handleSyncToHubSpot = async () => {
    if (selectedContacts.size === 0) {
      toast({
        title: 'No contacts selected',
        description: 'Please select at least one contact to sync',
        variant: 'destructive'
      });
      return;
    }

    setSyncing(true);
    try {
      await contactService.syncEngagementToHubSpot(
        stopId,
        Array.from(selectedContacts),
        syncNotes,
        customerId
      );
      
      // Refetch contacts directly
      fetchContacts();
      
      toast({
        title: 'Success',
        description: 'Engagement synced successfully'
      });
      
      setShowSyncDialog(false);
      setSelectedContacts(new Set());
      setSyncNotes('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error syncing to HubSpot:', error);
      toast({
        title: 'Error',
        description: 'Failed to sync engagement to HubSpot',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  const startEditingContact = (contact) => {
    setEditingContact(contact);
    setEditNotes(contact.notes || '');
    setEditStatus(contact.engagement_status);
  };

  const saveContactChanges = async () => {
    if (!editingContact) return;

    try {
      await contactService.updateContactAssociation(stopId, editingContact.contact_id, {
        notes: editNotes,
        engagement_status: editStatus
      });
      
      // Refetch contacts directly
      fetchContacts();
      
      toast({
        title: 'Success',
        description: 'Contact updated successfully'
      });
      
      setEditingContact(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: 'Error',
        description: 'Failed to update contact',
        variant: 'destructive'
      });
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getEngagementIcon = (type) => {
    switch (type) {
      case 'meeting':
        return <Calendar className="h-4 w-4" />;
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Calendar className="h-4 w-4 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Associated Contacts</CardTitle>
              <CardDescription>
                {contacts?.length || 0} contact(s) associated with this stop
              </CardDescription>
            </div>
            {(isRSM || isBM) && (contacts?.length || 0) > 0 && (
              <Button
                onClick={() => setShowSyncDialog(true)}
                disabled={selectedContacts.size === 0}
              >
                <Send className="h-4 w-4 mr-2" />
                Sync Engagement ({selectedContacts.size})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(!contacts || contacts.length === 0) ? (
            <p className="text-center text-muted-foreground py-8">
              No contacts associated with this stop
            </p>
          ) : (
            <div className="space-y-4">
              {contacts.map((contact) => (
                <Card key={contact.association_id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {(isRSM || isBM) && (
                        <Checkbox
                          checked={selectedContacts.has(contact.contact_id)}
                          onCheckedChange={() => toggleContactSelection(contact.contact_id)}
                        />
                      )}
                      <Avatar>
                        <AvatarFallback>{getInitials(contact.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact'}</h4>
                            {contact.engagement_count && (
                              <p className="text-xs text-muted-foreground">{contact.engagement_count} engagement(s)</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(contact.engagement_status)}
                            {(isRSM || isBM) && (
                              <>
                                {editingContact?.contact_id === contact.contact_id ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={saveContactChanges}
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => startEditingContact(contact)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {(isRSM || isBM) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeContact(contact.contact_id, contact.engagement_type)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <a href={`mailto:${contact.email}`} className="hover:underline">
                            {contact.email}
                          </a>
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="hover:underline">
                              {contact.phone}
                            </a>
                          )}
                        </div>
                        
                        {contact.position && (
                          <Badge variant="secondary" className="mt-1">
                            {contact.position}
                          </Badge>
                        )}
                        
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="outline" className="flex items-center space-x-1">
                            {getEngagementIcon(contact.engagement_type)}
                            <span>{contact.engagement_type}</span>
                          </Badge>
                          <Badge variant={contact.engagement_status === 'completed' ? 'success' : 'default'}>
                            {contact.engagement_status}
                          </Badge>
                        </div>
                        
                        {editingContact?.contact_id === contact.contact_id ? (
                          <div className="space-y-2 mt-3">
                            <Select value={editStatus} onValueChange={setEditStatus}>
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="planned">Planned</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Textarea
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              placeholder={`Add ${user?.role} notes...`}
                              rows={2}
                            />
                          </div>
                        ) : (
                          <>
                            {/* Latest Engagement */}
                            {contact.latest_engagement && (
                              <div className="mt-2 p-2 bg-muted/30 rounded">
                                <p className="text-xs font-medium text-muted-foreground">Latest Engagement:</p>
                                <p className="text-sm text-foreground">{contact.latest_engagement.notes}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(contact.latest_engagement.created_at).toLocaleDateString()} - {contact.latest_engagement.created_by}
                                </p>
                              </div>
                            )}
                            
                            {/* Role-specific notes */}
                            {contact.bm_notes && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-muted-foreground">Business Manager Notes:</p>
                                <p className="text-sm text-muted-foreground">{contact.bm_notes}</p>
                              </div>
                            )}
                            {contact.rsm_notes && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-muted-foreground">RSM Notes:</p>
                                <p className="text-sm text-muted-foreground">{contact.rsm_notes}</p>
                              </div>
                            )}
                            {contact.general_notes && !contact.bm_notes && !contact.rsm_notes && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-muted-foreground">Notes:</p>
                                <p className="text-sm text-muted-foreground">{contact.general_notes}</p>
                              </div>
                            )}
                            
                            {/* Associated stops info */}
                            {contact.associated_stops && contact.associated_stops.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-muted-foreground">Associated Stops:</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {contact.associated_stops.slice(0, 3).map((stop, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {stop.stop_name}
                                    </Badge>
                                  ))}
                                  {contact.associated_stops.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{contact.associated_stops.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync Engagement</DialogTitle>
            <DialogDescription>
              Add notes about your engagement with the selected contacts. This will create an activity record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Selected Contacts:</p>
              <div className="space-y-1">
                {(contacts || [])
                  .filter(c => selectedContacts.has(c.contact_id))
                  .map(c => (
                    <Badge key={c.contact_id} variant="secondary">
                      {c.name || 'Unknown Contact'}
                    </Badge>
                  ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Engagement Notes:</label>
              <Textarea
                value={syncNotes}
                onChange={(e) => setSyncNotes(e.target.value)}
                placeholder="Describe what was discussed, next steps, etc..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSyncToHubSpot} disabled={syncing}>
              {syncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Sync to HubSpot
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}